import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Inbox, Mail, Megaphone, Phone, RotateCcw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/site";

export default function AdminLeads() {
  const [leads, setLeads] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = () => {
    api
      .get("/leads")
      .then((res) => setLeads(res.data))
      .catch(() => {
        setLeads([]);
        toast.error("Couldn't load leads");
      });
  };

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (lead, status) => {
    try {
      await api.patch(`/leads/${lead.id}`, { status });
      setLeads((list) => list.map((l) => (l.id === lead.id ? { ...l, status } : l)));
      toast.success(status === "handled" ? "Marked as handled" : "Moved back to new");
    } catch {
      toast.error("Couldn't update lead");
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/leads/${deleting.id}`);
      toast.success("Lead deleted");
      setDeleting(null);
      load();
    } catch {
      toast.error("Couldn't delete lead");
    }
  };

  const newCount = leads?.filter((l) => l.status === "new").length || 0;

  return (
    <div data-testid="admin-leads-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">Taster enquiries (leads)</h1>
          <p className="text-muted-foreground mt-1">
            Parents who asked for a free taster from the ad landing page.{" "}
            {leads && (newCount > 0 ? `${newCount} waiting for a reply.` : "All caught up!")}
          </p>
        </div>
      </div>

      <div className="mt-6 space-y-4">
        {leads === null ? (
          [...Array(3)].map((_, i) => <Skeleton key={i} className="h-36 rounded-[4px]" />)
        ) : leads.length === 0 ? (
          <Card className="rounded-[4px]">
            <CardContent className="p-12 text-center" data-testid="admin-leads-empty">
              <Inbox className="h-10 w-10 mx-auto text-muted-foreground" />
              <p className="font-display text-2xl font-semibold mt-4">No taster enquiries yet</p>
              <p className="text-muted-foreground text-sm mt-2 max-w-sm mx-auto">
                When a parent submits the free taster form on your landing page, their details will land here — and
                you&rsquo;ll get an email straight away.
              </p>
            </CardContent>
          </Card>
        ) : (
          leads.map((lead) => (
            <Card
              key={lead.id}
              className={`rounded-[4px] ${lead.status === "new" ? "border-[hsl(var(--kerrys-coral))]/60" : ""}`}
              data-testid="admin-lead-card"
            >
              <CardContent className="p-5 sm:p-6">
                <div className="flex flex-col md:flex-row md:items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <Badge
                        variant="outline"
                        className={`rounded-[4px] font-semibold ${
                          lead.status === "new"
                            ? "bg-[hsl(var(--kerrys-coral))]/15 text-[hsl(18,80%,38%)] border-[hsl(var(--kerrys-coral))]/50"
                            : "bg-secondary text-muted-foreground"
                        }`}
                        data-testid="admin-lead-status-badge"
                      >
                        {lead.status === "new" ? "New" : "Handled"}
                      </Badge>
                      {lead.source && (
                        <Badge variant="outline" className="rounded-[4px] font-medium gap-1">
                          <Megaphone className="h-3 w-3" /> {lead.source}
                        </Badge>
                      )}
                      <span className="text-xs text-muted-foreground">{fmtDate(lead.created_at)}</span>
                    </div>
                    <p className="font-display text-xl sm:text-2xl font-semibold mt-2.5" data-testid="admin-lead-name">
                      {lead.parent_name}
                      {lead.child_age && <span className="text-muted-foreground font-sans text-base font-normal"> &middot; child age {lead.child_age}</span>}
                    </p>
                    {lead.interest && (
                      <p className="text-sm mt-1.5">
                        <span className="text-muted-foreground">Interested in: </span>
                        <span className="font-semibold">{lead.interest}</span>
                      </p>
                    )}
                    {lead.message && (
                      <p className="text-sm text-muted-foreground mt-2 leading-relaxed whitespace-pre-wrap">{lead.message}</p>
                    )}
                    <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-3 text-sm">
                      {lead.email && (
                        <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-1.5 text-primary font-medium hover:underline" data-testid="admin-lead-email-link">
                          <Mail className="h-3.5 w-3.5" /> {lead.email}
                        </a>
                      )}
                      {lead.phone && (
                        <a href={`tel:${lead.phone}`} className="inline-flex items-center gap-1.5 text-primary font-medium hover:underline" data-testid="admin-lead-phone-link">
                          <Phone className="h-3.5 w-3.5" /> {lead.phone}
                        </a>
                      )}
                    </div>
                    {lead.utm && (
                      <p className="text-xs text-muted-foreground/80 mt-2 font-mono truncate" title={lead.utm}>
                        campaign: {lead.utm}
                      </p>
                    )}
                  </div>
                  <div className="flex md:flex-col gap-2 shrink-0">
                    {lead.status === "new" ? (
                      <Button size="sm" onClick={() => setStatus(lead, "handled")} className="rounded-[4px] font-semibold" data-testid="admin-lead-handle-button">
                        <CheckCircle2 className="h-4 w-4 mr-1.5" /> Mark handled
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => setStatus(lead, "new")} className="rounded-[4px]" data-testid="admin-lead-reopen-button">
                        <RotateCcw className="h-4 w-4 mr-1.5" /> Reopen
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setDeleting(lead)}
                      className="rounded-[4px] text-destructive hover:text-destructive"
                      data-testid="admin-lead-delete-button"
                    >
                      <Trash2 className="h-4 w-4 mr-1.5" /> Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this lead?</AlertDialogTitle>
            <AlertDialogDescription>
              The taster enquiry from {deleting?.parent_name} will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-[4px]">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="rounded-[4px] bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="admin-lead-confirm-delete-button">
              Delete lead
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
